import { useState, useRef, useEffect } from 'react'
import { useSwipe } from '@hooks/useSwipe'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { useRovingTabIndex } from '@hooks/useRovingTabIndex'
import { useEscapeKey } from '@hooks/useEscapeKey'
import { useLanguage } from '@context/LanguageContext'
import { cx } from '@utils/cx'
import styles from './Carousel.module.css'
import baseStyles from '@styles/modalBase.module.css'

/**
 * Carousel - Reusable component for displaying a list of items with navigation controls.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of objects with structure: { filename/code, name, translationKey?, image? }
 * @param {number} props.currentIndex - Index of the currently selected item
 * @param {function} props.onSelect - Callback executed when an item is selected (item) => void
 * @param {function} props.onClose - Callback executed when the modal is closed (only for variant="modal")
 * @param {string} props.variant - Display variant: "modal" | "inline"
 * @param {string} props.imageType - Image type: "avatar" | "flag"
 * @param {boolean} props.showName - Whether to show the name below the image
 * @param {string} props.ariaLabel - Label for dialog accessibility
 * @param {string} props.instructions - Instructions for screen readers
 * @param {function} props.getDisplayName - Function to get the display name (item, t) => string
 */
export default function Carousel ({
  items = [],
  currentIndex: initialIndex = 0,
  onSelect,
  onClose,
  variant = 'modal',
  imageType = 'avatar',
  showName = true,
  ariaLabel,
  instructions,
  getDisplayName
}) {
  const { t } = useLanguage()
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const selectedItem = items[currentIndex] || items[0]

  const modalRef = useRef(null)
  useFocusTrap(modalRef)
  useEscapeKey(onClose, variant === 'modal' && typeof onClose === 'function')

  // Map imageType to style class
  const imageTypeStyles = {
    avatar: styles.imageAvatar,
    flag: styles.imageFlag
  }

  const { getTabIndex, getItemRef, handleKeyDown } = useRovingTabIndex({
    itemCount: 3,
    navigationStrategy: 'linear',
    onActivate: (index) => {
      if (index === 0) {
        goToPrev() // Prev button
      } else if (index === 1) {
        handleSelect() // Image selection
      } else if (index === 2) {
        goToNext() // Next button
      }
    }
  })

  const goToNext = () => {
    setCurrentIndex((prev) =>
      prev === items.length - 1 ? 0 : prev + 1
    )
  }

  const goToPrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? items.length - 1 : prev - 1
    )
  }

  const handleSelect = () => {
    if (onSelect && selectedItem) {
      onSelect(selectedItem)
    }
  }

  const { handleTouchStart, handleTouchEnd } = useSwipe(goToNext, goToPrev)

  // Focus management for modal variant
  useEffect(() => {
    if (variant === 'modal' && modalRef.current) {
      modalRef.current.focus()
    }
  }, [variant])

  const getImageSrc = (item) => {
    if (imageType === 'avatar') {
      return `/avatars/${item.filename}`
    } else if (imageType === 'flag') {
      return item.image || `/assets/flags/${item.code}.webp`
    }
    return item.image || '/avatars/avatar-default.webp'
  }

  const getImageAlt = (item) => {
    const displayName = getDisplayName ? getDisplayName(item) : item.name
    if (imageType === 'avatar') {
      return t.core.access.carousel.avatarAlt.replace('{name}', displayName)
    } else if (imageType === 'flag') {
      const countryName = t.core.countries?.[item.code] || displayName
      return t.core.access.carousel.flagAlt.replace('{name}', countryName)
    }
    return displayName
  }

  const renderContent = () => (
    <>
      {/* Hidden instructions - read once opened */}
      <div id='carousel-select' className='sr-only'>
        {instructions}
      </div>

      <button
        ref={getItemRef(0)}
        tabIndex={getTabIndex(0)}
        className={styles.nav}
        onClick={goToPrev}
        aria-label={t.core.access.common.previous}
        title={t.core.access.common.previous}
      >
        ◀
      </button>

      <div
        className={styles.imageContainer}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          ref={getItemRef(1)}
          tabIndex={getTabIndex(1)}
          className={styles.imageButton}
          onClick={handleSelect}
          aria-label={getDisplayName ? getDisplayName(selectedItem) : selectedItem?.name}
        >
          <img
            src={getImageSrc(selectedItem)}
            alt={getImageAlt(selectedItem)}
            className={cx(
              styles.image,
              imageTypeStyles[imageType]
            )}
            aria-hidden='true'
          />
        </button>
        {showName && selectedItem && (
          <span
            className={styles.name}
            aria-hidden='true'
          >
            {getDisplayName ? getDisplayName(selectedItem) : selectedItem.name}
          </span>
        )}
      </div>

      <button
        ref={getItemRef(2)}
        tabIndex={getTabIndex(2)}
        className={styles.nav}
        onClick={goToNext}
        aria-label={t.core.access.common.next}
        title={t.core.access.common.next}
      >
        ▶
      </button>
    </>
  )

  if (variant === 'modal') {
    return (
      <div
        className={baseStyles.overlayCenter}
        role='dialog'
        aria-modal='true'
        aria-label={ariaLabel}
      >
        <div
          className={styles.modal}
          ref={modalRef}
          tabIndex='-1'
          onKeyDown={handleKeyDown}
          aria-describedby='carousel-select'
        >
          {renderContent()}
        </div>
      </div>
    )
  }

  // Inline variant
  return (
    <div
      className={styles.inline}
      onKeyDown={handleKeyDown}
      aria-describedby='carousel-select'
    >
      {renderContent()}
    </div>
  )
}
